import { Component } from '@angular/core';
import { Auth, signInWithEmailLink, authState, signInWithEmailAndPassword, User, signInWithPopup, GoogleAuthProvider, OAuthProvider, fetchSignInMethodsForEmail, linkWithCredential, OAuthCredential} from '@angular/fire/auth';
import { FormControl, FormGroup } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { EMPTY, Observable } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import axios from 'axios';
import { environment } from '../../environments/environment';
import { TwoFactorAuthService } from '../services/two-factor-auth.service';
import { AuthService } from '../auth/auth.service';
declare var $: any; // Declara $ para usar jQuery dentro de este archivo.



@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrl: './login.component.css'
})
export class LoginComponent {
  public readonly user: Observable<User | null> = EMPTY;

  loginBtnText="Sign in"
  errorMessage = ""
  successMessage = ""
  loading = false
  showPassword = false
  isDarkModeActive = false

  showEmailForm = false;

  // Pending Microsoft credential waiting for email/password link
  pendingMicrosoftCred: OAuthCredential | null = null;

  // 2FA
  show2FAModal = false;
  pendingNavigation = false;

  // Onboarding mode (from self-service landing)
  isOnboardingMode = false;

  loginForm: FormGroup;
  domain = window.location.href;


  constructor(public auth: Auth, private router : Router, private route: ActivatedRoute, private cookieService: CookieService, private twoFactorService: TwoFactorAuthService, private authService: AuthService){
    //SETUP THE FORM
    this.loginForm = new FormGroup({
      email: new FormControl(''),
      password: new FormControl('')
    });

    // Check if coming from onboarding landing page
    this.route.queryParams.subscribe(params => {
      this.isOnboardingMode = params['source'] === 'onboarding';
      if (this.isOnboardingMode) {
        console.log('🚀 LoginComponent: Onboarding mode activated');
      }
    });

    // Initialize dark mode based on saved preference or system preference
    this.initializeDarkMode();

    //READ THE AUTH STATE
    if(auth){
      //console.log("user",authState(this.auth))
      this.user=authState(this.auth);
      authState(this.auth).pipe().subscribe(isLoggedIn => {

      });
    }

    //quitar clase hidden pasados 2 segundos
    setTimeout(() => {
      //animate opacity from 0 to 1
      document.getElementById("login-zone")?.classList.remove("opacity-0");

    }, 600);

  }

  onInit(): void {
    //console.log("user",
    $("#mz-nav").addClass("hidden");
  }


  onSubmit(): void{
    this.loginBtnText="connecting..."
    signInWithEmailAndPassword(this.auth, this.loginForm.value.email, this.loginForm.value.password)
    .then(async (data) => {
      this.loginBtnText="Sign in"

      // If the user came from a failed Microsoft SSO, link that credential now
      if (this.pendingMicrosoftCred) {
        try {
          await linkWithCredential(data.user, this.pendingMicrosoftCred);
          console.log('✅ Microsoft account linked successfully');
        } catch (linkErr) {
          console.warn('Microsoft link failed (may already be linked):', linkErr);
        }
        this.pendingMicrosoftCred = null;
      }

      this.cookieService.set('token',await data.user.getIdToken());
      await this.handlePostLogin();
    })
    .catch((error) =>{
      this.loginBtnText="Sign in"
      console.log("error: ",error.message)
      this.errorMessage = "Wrong user or password. Please try again.";
    });
  }

  private async handlePostLogin(): Promise<void> {
    try {
      console.log('🔐 LoginComponent: Checking 2FA status...');
      // Check if 2FA is enabled for this user
      const status = await this.twoFactorService.getStatus();
      console.log('🔐 LoginComponent: 2FA status:', status);
      
      if (status.enabled) {
        console.log('🔐 LoginComponent: 2FA is enabled, showing modal');
        // Show 2FA modal
        this.pendingNavigation = true;
        this.show2FAModal = true;
      } else {
        console.log('🔐 LoginComponent: 2FA not enabled, proceeding to dashboard');
        // No 2FA, proceed to dashboard
        this.navigateToDashboard();
      }
    } catch (error) {
      console.error('🔐 LoginComponent: Error checking 2FA status:', error);
      // If we can't check 2FA status, just proceed (likely not an admin)
      this.navigateToDashboard();
    }
  }

  private navigateToDashboard(): void {
    $("#mz-nav").removeClass("hidden");

    const onboardingData = this.authService.getOnboardingData();
    if (onboardingData?.prompt) {
      this.router.navigate(['/agents/new'], {
        queryParams: { onboarding: 'true' }
      });
    } else {
      this.router.navigate(['/playbooks']);
    }
  }

  on2FAVerified(): void {
    this.show2FAModal = false;
    this.pendingNavigation = false;
    this.navigateToDashboard();
  }

  on2FACancelled(): void {
    this.show2FAModal = false;
    this.pendingNavigation = false;
    // Sign out the user since they didn't complete 2FA
    this.auth.signOut();
    this.errorMessage = "Two-factor authentication is required for admin accounts.";
  }

  async onForgotPassword(): Promise<void> {
    const emailValue = this.loginForm.get('email')?.value;
    if (!emailValue) {
      this.errorMessage = "Please enter your email address.";
      this.successMessage = "";
      return;
    }

    this.loading = true;
    this.errorMessage = "";
    this.successMessage = "";

    try {
      const response = await axios.post(`${environment.server}/reset-password/request`, { email: emailValue });
      if (response.data.success) {
        this.successMessage = "An email has been sent to you with instructions to reset your password.";
      } else {
        this.errorMessage = response.data.message || "Failed to send password reset email";
      }
    } catch (error: any) {
      console.error("Error sending reset email:", error);
      this.errorMessage = error.response?.data?.message || "There was an error sending the email. Please try again.";
    } finally {
      this.loading = false;
    }
  }

  onSignInWithGoogle(): void {
    const provider = new GoogleAuthProvider();
    signInWithPopup(this.auth, provider)
      .then(async (result) => {
        this.loginBtnText = "Sign in";

        // If the user came here from a failed Microsoft SSO (account already
        // exists with Google), link that pending credential now. Using
        // linkWithCredential (not linkWithPopup) avoids opening a second
        // popup, which browsers block since it's not a direct user gesture.
        if (this.pendingMicrosoftCred) {
          try {
            await linkWithCredential(result.user, this.pendingMicrosoftCred);
            console.log('✅ Microsoft account linked successfully');
          } catch (linkErr) {
            console.warn('Microsoft link failed (may already be linked):', linkErr);
          }
          this.pendingMicrosoftCred = null;
        }

        this.cookieService.set('token', await result.user.getIdToken());
        await this.handlePostLogin();
      })
      .catch((error) => {
        this.loginBtnText = "Sign in";
        console.log("error: ", error.message);
        this.errorMessage = "Failed to sign in with Google. Please try again.";
      });
  }

  onSignInWithMicrosoft(): void {
    const provider = new OAuthProvider('microsoft.com');
    signInWithPopup(this.auth, provider)
      .then(async (result) => {
        this.loginBtnText = "Sign in";
        this.cookieService.set('token', await result.user.getIdToken());
        await this.handlePostLogin();
      })
      .catch(async (error) => {
        this.loginBtnText = "Sign in";
        console.log("Microsoft sign-in error:", error.code, error.message);

        if (error.code === 'auth/account-exists-with-different-credential') {
          // The email already exists linked to another provider (e.g. Google).
          // Sign in with that provider first, then link the Microsoft credential.
          const pendingCred: OAuthCredential = OAuthProvider.credentialFromError(error) as OAuthCredential;
          const email = error.customData?.email;

          if (!email || !pendingCred) {
            this.errorMessage = "This email is already registered with a different sign-in method. Please use the original method.";
            return;
          }

          try {
            const methods = await fetchSignInMethodsForEmail(this.auth, email);

            if (methods.includes('google.com')) {
              // Account registered with Google — store the Microsoft credential
              // and ask the user to click "Continue with Google" themselves.
              // We can't open the Google popup automatically here because it's
              // not a direct user gesture (it happens after the async
              // fetchSignInMethodsForEmail call), so browsers block it as
              // auth/popup-blocked. onSignInWithGoogle() links the credential
              // (via linkWithCredential, no popup needed) after sign-in.
              this.pendingMicrosoftCred = pendingCred;
              this.errorMessage = "This email is already registered with Google. Click \"Continue with Google\" below to link your Microsoft account.";

            } else if (methods.includes('password')) {
              // Account registered with email/password — store the Microsoft credential,
              // pre-fill the email field and open the password form so the user can
              // authenticate; onSubmit() will link the credential after sign-in.
              this.pendingMicrosoftCred = pendingCred;
              this.loginForm.patchValue({ email });
              this.showEmailForm = true;
              this.errorMessage = "This email is already registered with a password. Enter your password below to link your Microsoft account.";

            } else {
              this.errorMessage = "This email is already registered. Please sign in with your original method.";
            }
          } catch (linkError: any) {
            console.log("Account linking error:", linkError);
            this.errorMessage = "Failed to check existing sign-in methods. Please try again.";
          }
        } else {
          this.errorMessage = "Failed to sign in with Microsoft. Please try again.";
        }
      });
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  toggleDarkMode(): void {
    this.isDarkModeActive = !this.isDarkModeActive;
    if (this.isDarkModeActive) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }

  private initializeDarkMode(): void {
    // Check if user has a saved preference
    const savedTheme = localStorage.getItem('theme');
    let shouldBeDark = false;
    
    if (savedTheme) {
      shouldBeDark = savedTheme === 'dark';
    } else {
      // Default to system preference
      shouldBeDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    }

    // Apply the theme
    if (shouldBeDark) {
      document.documentElement.classList.add('dark');
      this.isDarkModeActive = true;
    } else {
      document.documentElement.classList.remove('dark');
      this.isDarkModeActive = false;
    }

    console.log('Dark mode initialized:', shouldBeDark, 'Saved theme:', savedTheme);
  }
}
